"use client";

import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
} from "motion/react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { TextRoll } from "@/components/motion/text-roll";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import type { NavItem } from "../../_constants/navigation";

import { CornerBrackets } from "./corner-brackets";
import { LocaleSwitcher } from "./locale-switcher";
import { MobileMenu } from "./mobile-menu";
import { NavLogo } from "./nav-logo";
import { ThemeSwitcher } from "./theme-switcher";

// =============================================================================

export function isActivePath(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

// =============================================================================

export function Navbar({ items }: { items: NavItem[] }) {
  const t = useTranslations("Navigation");
  const pathname = usePathname();
  const { scrollY } = useScroll();
  const [isHidden, setIsHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  // Hide while scrolling down, show again as soon as the visitor scrolls up
  useMotionValueEvent(scrollY, "change", (current) => {
    const previous = scrollY.getPrevious() ?? 0;
    setIsScrolled(current > 24);
    if (isMenuOpen) return;
    setIsHidden(current > previous && current > 240);
  });

  const activeHref =
    items.find((item) => isActivePath(pathname, item.href))?.href ?? null;
  const bracketHref = hovered ?? activeHref;

  return (
    <>
      <motion.header
        initial={{ y: "-110%" }}
        animate={{ y: isHidden ? "-110%" : "0%" }}
        transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
        style={{ viewTransitionName: "site-header" }}
        className="fixed inset-x-0 top-0 z-50"
      >
        {/* Blurred backdrop once the page is scrolled */}
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 bg-gradient-to-b from-background/85 via-background/60 to-transparent backdrop-blur-[2px] transition-opacity duration-500",
            isScrolled ? "opacity-100" : "opacity-0"
          )}
        />

        <div className="relative flex h-[4.625rem] items-center justify-between px-5 sm:px-[1.875rem]">
          <Link
            href="/"
            aria-label={t("backHome")}
            className="relative z-[60] -m-2 p-2 text-foreground"
          >
            <NavLogo className="w-[1.875rem]" />
          </Link>

          <nav
            aria-label={t("mainNavigation")}
            className="hidden items-center lg:flex"
          >
            <ul
              className="flex items-center gap-x-1"
              onPointerLeave={(event) => {
                if (event.pointerType === "mouse") setHovered(null);
              }}
            >
              {items.map((item, index) => {
                const isActive = item.href === activeHref;
                return (
                  <motion.li
                    key={item.key}
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.35 + index * 0.05,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  >
                    <motion.div
                      initial="rest"
                      whileHover="hover"
                      animate="rest"
                      className="relative"
                    >
                      <Link
                        href={item.href}
                        aria-current={isActive ? "page" : undefined}
                        // Mouse only: on a touch screen (tablet), moving the
                        // brackets on tap turns the tap into a hover
                        onPointerEnter={(event) => {
                          if (event.pointerType === "mouse")
                            setHovered(item.href);
                        }}
                        onFocus={() => setHovered(item.href)}
                        onBlur={() => setHovered(null)}
                        className={cn(
                          "relative block px-[1.1rem] py-2 text-sm transition-colors xl:px-6",
                          isActive || hovered === item.href
                            ? "text-foreground"
                            : "text-foreground/75"
                        )}
                      >
                        <TextRoll>{t(item.key)}</TextRoll>
                        {bracketHref === item.href && (
                          <motion.span
                            layoutId="navbar-brackets"
                            className="absolute inset-x-[0.55rem] inset-y-0.5 xl:inset-x-[0.85rem]"
                            transition={{
                              type: "spring",
                              stiffness: 380,
                              damping: 30,
                            }}
                          >
                            <CornerBrackets className="inset-0" />
                          </motion.span>
                        )}
                      </Link>
                    </motion.div>
                  </motion.li>
                );
              })}
            </ul>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="ml-4 flex items-center gap-2 border-l border-foreground/10 pl-5"
            >
              <ThemeSwitcher />
              <LocaleSwitcher />
            </motion.div>
          </nav>

          <MenuToggle
            isOpen={isMenuOpen}
            onToggle={() => setIsMenuOpen((open) => !open)}
            label={isMenuOpen ? t("closeMenu") : t("openMenu")}
          />
        </div>
      </motion.header>

      <AnimatePresence>
        {isMenuOpen && (
          <MobileMenu
            items={items}
            activeHref={activeHref}
            onClose={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// =============================================================================

type MenuToggleProps = {
  isOpen: boolean;
  onToggle: () => void;
  label: string;
};

function MenuToggle({ isOpen, onToggle, label }: MenuToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-expanded={isOpen}
      aria-controls="mobile-menu"
      className="relative z-[60] -mr-2 grid size-11 place-items-center lg:hidden"
    >
      <span className="relative block h-3 w-7">
        <motion.span
          animate={
            isOpen ? { top: "50%", rotate: 45 } : { top: "0%", rotate: 0 }
          }
          transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          className="absolute left-0 block h-px w-full origin-center bg-foreground"
        />
        <motion.span
          animate={
            isOpen ? { top: "50%", rotate: -45 } : { top: "100%", rotate: 0 }
          }
          transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          className="absolute left-0 block h-px w-full origin-center bg-foreground"
        />
      </span>
    </button>
  );
}

// =============================================================================
